import classNames from "classnames";
import React, {
    FormEvent, ReactElement, useEffect, useState,
} from "react";
import { Form, InputGroup } from "react-bootstrap";
import { RiBuildingLine, RiGroupLine } from "react-icons/ri";
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { ErrorResponse } from "@apollo/client/link/error";
import useRouting, { Paths, useParams } from "../../../contexts/Routing";

import {
    GetCurrentPortalDocument, MicrosoftTeamsAssociationType, useFinishIntegrationMicrosoftTeamMutation,
    useGetArticleQuery,
} from "../../../__generated__/api";
import { ARTICLES } from "../../../components/help/Help";
import { HtmlRenderer } from "../../../components/html-render/HtmlRenderer";
import { LoadingIcon } from "../../../components/loading/Loading";
import Button from "../../../components/theme/button/Button";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import Alert from "../../../components/theme/alert/Alert";
import useCurrentSession from "../../../contexts/session";

const TOP_LEVEL_DOMAIN = process.env.REACT_APP_TOP_LEVEL_DOMAIN;
const TOP_LEVEL_PATH = process.env.REACT_APP_TOP_LEVEL_DOMAIN_PATH as string;

enum Step {
    SELECT_PURPOSE = "SELECT_PURPOSE",
    PARTNER = "PARTNER",
    TEAM = "TEAM",
    ASSOCIATE = "ASSOCIATE",
    FINISHED = "FINISHED"
}
export default function TeamsInstallCompleted() : ReactElement | null {
    const { getSearchParams, historyPushWithSearch } = useRouting();
    const { token } = useParams(Paths.ONBOARDING_TEAMS_INSTALLED);
    const { isAssociated, organization } = getSearchParams(Paths.ONBOARDING_TEAMS_INSTALLED);

    const [nextStep, setNextStep] = useState<Step>();
    const [step, setStep] = useState<Step>(isAssociated ? Step.ASSOCIATE : Step.SELECT_PURPOSE);

    const [domain, setDomain] = useState("");
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState("");

    const { isLoggedIn } = useCurrentSession();

    const [finishIntegration] = useFinishIntegrationMicrosoftTeamMutation();

    const { data: article, loading: isArticleLoading } = useGetArticleQuery({
        skip: step !== Step.PARTNER,
        variables: {
            id: ARTICLES.ONBOARDING_TEAMS_INSTALLED_VENDOR,
        },
    });

    const submit = (evt: FormEvent) => {
        evt.preventDefault();
        setIsValidating(true);
        LoggingHandler.instance.nextTick(async () => {
            const fullDomain = `https://${domain}.${TOP_LEVEL_DOMAIN}`;
            const apiDomain = new URL(TOP_LEVEL_PATH, fullDomain);

            const client = new ApolloClient({
                link: createHttpLink({
                    uri: apiDomain.toString(),
                    credentials: "include",
                }),
                cache: new InMemoryCache(),
            });

            try {
                await client.query({
                    query: GetCurrentPortalDocument,
                });
            } catch (err) {
                const { networkError } = err as ErrorResponse;
                if (networkError) {
                    setIsValidating(false);
                    setError("There was an issue validating this domain. Please make sure you enter it correctly.");
                    return;
                }
            }

            const url = new URL(window.location.pathname, fullDomain);
            url.searchParams.append("isAssociated", "true");
            url.searchParams.append("organization", organization);
            window.location.href = url.toString();
        });
    };

    const confirmAssociation = (evt: FormEvent | undefined, type: MicrosoftTeamsAssociationType) => {
        evt?.preventDefault();
        setIsValidating(true);
        LoggingHandler.instance.nextTick(async () => {
            try {
                await finishIntegration({
                    variables: {
                        settings: {
                            token,
                            type,
                        },
                    },
                });
            } catch (err) {
                const { networkError } = err as ErrorResponse;
                if (networkError) {
                    setIsValidating(false);
                    setError("There was an issue validating this domain. Please make sure you enter it correctly.");
                    return;
                }
                throw err;
            }
            setStep(Step.FINISHED);
        });
    };

    useEffect(() => {
        if (step === Step.PARTNER) {
            confirmAssociation(undefined, MicrosoftTeamsAssociationType.Vendor);
        }
    }, [step === Step.PARTNER]);

    if (isAssociated && !isLoggedIn) {
        historyPushWithSearch(Paths.LOGIN, { redirect: window.location.href, error: "" });
    }

    return (

        <div className="full-page">
            <div className="centered-container centered-container-lg">
                {error && (
                    <Alert variant="danger" className="mt-2">
                        {error}
                    </Alert>
                )}
                {step === Step.SELECT_PURPOSE && (
                    <div>
                        <div className="d-flex flex-row justify-items-between align-items-center w-100 mt-3">
                            <div>
                                <h4>Welcome, How do you want to use Foqal?</h4>
                                <p>
                                    Thank you for installing Foqal&apos;s Microsoft Teams integration. Please select how you plan
                                    to work with Foqal. Do you want to use Foqal with your team or to communicate with
                                    a partner or vendor?
                                </p>
                            </div>

                        </div>
                        <div className="d-flex list-group flex-row w-100 gap-2 mb-2">
                            <div className="text-center d-flex flex-column col-6">
                                <button
                                    type="button"
                                    onClick={() => setNextStep(Step.TEAM)}
                                    className={classNames(
                                        "list-group-item list-group-item-action flex-grow-1 p-3 rounded hover-shadow",
                                        {
                                            "bg-blue-light text-white": nextStep === Step.TEAM,
                                        },
                                    )}
                                >
                                    <div className="mb-2">
                                        <RiBuildingLine size="40px" />
                                    </div>
                                    <strong>Your Team</strong>
                                    <p>
                                        I am using Foqal with my team or I would like to talk to my customers using Foqal.
                                    </p>
                                </button>

                            </div>
                            <div className="text-center d-flex flex-column col-6">
                                <button
                                    type="button"
                                    onClick={() => setNextStep(Step.PARTNER)}
                                    className={classNames(
                                        "list-group-item list-group-item-action flex-grow-1 p-3 rounded hover-shadow",
                                        {
                                            "bg-blue-light text-white": nextStep === Step.PARTNER,
                                        },
                                    )}
                                >

                                    <div className="mb-2">
                                        <RiGroupLine size="40px" />
                                    </div>
                                    <strong>Vendor or Partner</strong>
                                    <p>
                                        I was asked by my partner or vendor to install Foqal to work with them.
                                    </p>
                                </button>

                            </div>

                        </div>

                    </div>
                )}

                {step === Step.PARTNER && (
                    isArticleLoading ? <LoadingIcon /> : (
                        <div>
                            <h5>{article?.article?.title}</h5>
                            <HtmlRenderer html={article?.article?.body.html as string} />
                        </div>
                    )
                )}

                {step === Step.TEAM && (
                    <div>
                        <h5>What is your Foqal instance?</h5>
                        <p>
                            Please enter the website you use to access Foqal. It should look something like my-team.foqal.app.
                        </p>
                        <form className="w-50 mx-auto" onSubmit={submit}>
                            <InputGroup>
                                <InputGroup.Text>https://</InputGroup.Text>
                                <Form.Control
                                    placeholder="example"
                                    aria-label="Foqal Domain"
                                    required
                                    onChange={evt => setDomain(evt.target.value)}
                                    value={domain}
                                />
                                <InputGroup.Text>.foqal.app</InputGroup.Text>
                            </InputGroup>

                            <div className="col d-flex justify-content-end mt-2">

                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="btn-sm ms-2"
                                    size="large"
                                    disabled={isValidating}
                                >
                                    {isValidating ? (
                                        <>
                                            <LoadingIcon size="20px" className="me-2" />
                                            Validating...
                                        </>
                                    ) : "Submit"}

                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {step === Step.ASSOCIATE && (
                    <div>
                        <h5>
                            Confirm Association with
                            {" "}
                            &quot;
                            {organization}
                            &quot;
                            ?
                        </h5>

                        <p>
                            Would you like to associate your workspace with Microsoft Teams:
                            {" "}
                            {organization}
                            ?
                        </p>

                        <Alert variant="primary">
                            Microsoft Teams:
                            {" "}
                            {organization}
                        </Alert>

                        <div className="d-flex justify-content-around w-100">

                            <form
                                className="col d-flex justify-content-end mt-2"
                                onSubmit={evt => confirmAssociation(evt, MicrosoftTeamsAssociationType.Team)}
                            >
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="btn-sm ms-2"
                                    disabled={isValidating}
                                >
                                    {isValidating ? (
                                        <>
                                            <LoadingIcon size="20px" className="me-2" />
                                            Associating...
                                        </>
                                    ) : "Confirm Association"}

                                </Button>
                            </form>

                        </div>

                    </div>
                )}

                {step === Step.FINISHED && (
                    <div>
                        <h5>🎉 Done</h5>
                        <p>
                            Finished associating Microsoft Teams with your Foqal account.
                        </p>
                    </div>
                )}

                {nextStep && (
                    <div className="mt-2 justify-content-end d-flex w-100">
                        <Button
                            onClick={() => {
                                setStep(nextStep);
                                setNextStep(undefined);
                            }}
                            variant="primary"
                            size="large"
                        >
                            Next
                        </Button>
                    </div>
                )}

            </div>
        </div>

    );
}
